"use client";

import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  MapPin,
  WashingMachine,
  Clock,
  Check,
  X,
  Wallet,
  CalendarCheck,
  Inbox,
  ShieldCheck,
} from "lucide-react";

/**
 * Showcase op de landing: het monteur-dashboard zoals Arjan Mols het ziet —
 * toegewezen jobs van vandaag, een gerichte aanbieding van EP Heinz en zijn
 * verdiensten. Statische mockup (geen API), gemodelleerd op /monteur.
 */

type Job = {
  nr: string;
  apparaat: string;
  merk: string;
  plaats: string;
  storing: string;
  tijd: string;
  tarief: number;
  status: "Geaccepteerd" | "Onderweg";
};

const TOEGEWEZEN: Job[] = [
  { nr: "EH-0204", apparaat: "Wasmachine", merk: "Bosch", plaats: "Utrecht", storing: "Centrifugeert niet", tijd: "Morgen · 13:00–15:00", tarief: 92, status: "Geaccepteerd" },
  { nr: "EH-0198", apparaat: "Vaatwasser", merk: "Siemens", plaats: "Nieuwegein", storing: "Pompt niet af", tijd: "Vandaag · 15:30", tarief: 96, status: "Onderweg" },
];

const VERDIENSTEN = [
  { label: "Vandaag", bedrag: 855 },
  { label: "Deze week", bedrag: 2565 },
  { label: "Deze maand", bedrag: 10260 },
];

const STATUS_PILL: Record<Job["status"], string> = {
  Geaccepteerd: "bg-[#E6FAF1] text-[#0D7A4A]",
  Onderweg: "bg-[#F1EFE9] text-[var(--ink-2)]",
};

function euro(n: number) {
  return "€" + n.toLocaleString("nl-NL");
}

export default function MonteurDashboardShowcase() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-14 sm:px-6 sm:py-24">
      <p className="mb-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
        Voor monteurs
      </p>
      <h2 className="mb-3 text-center text-[clamp(24px,4vw,36px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)] sm:mb-4">
        Jouw werkdag op één scherm
      </h2>
      <p className="mx-auto mb-7 max-w-xl text-center text-[14px] leading-[1.65] text-[var(--ink-2)] sm:mb-12 sm:text-[15px] sm:leading-[1.7]">
        Toegewezen klussen, gerichte aanbiedingen die écht bij je passen en je
        verdiensten — alles op één plek. Accepteren doe je met één tik.
      </p>

      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface-raised)] shadow-[0_10px_36px_-16px_rgba(11,14,12,0.12)]"
      >
        {/* Dashboard-kop: monteur */}
        <div className="flex items-center gap-3 border-b border-[var(--line)] bg-[var(--surface)] px-4 py-3.5 sm:px-6 sm:py-4">
          <img
            src="/images/monteur-portret.jpg"
            alt="Profielfoto monteur"
            className="h-11 w-11 shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
          />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold leading-tight text-[var(--ink)]">Arjan Mols</p>
            <p className="flex items-center gap-1 text-[12px] text-[var(--ink-3)]">
              <MapPin size={12} strokeWidth={1.75} />
              Utrecht
            </p>
          </div>
          <div className="ml-auto flex items-center gap-4">
            {/* Capaciteit deze week */}
            <div className="hidden text-right sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Deze week</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[13px] font-semibold tabular-nums text-[var(--ink)]">3 / 5</span>
                <span className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className="h-3 w-1.5 rounded-full"
                      style={{ backgroundColor: i < 3 ? "#14C07A" : "var(--line)" }}
                    />
                  ))}
                </span>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-[color-mix(in_srgb,var(--brand)_14%,transparent)] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--brand)]">
              Actief
            </span>
          </div>
        </div>

        {/* Body: toegewezen + aanbieding */}
        <div className="grid gap-4 p-4 sm:gap-5 sm:p-6 lg:grid-cols-2">
          {/* Toegewezen vandaag */}
          <div>
            <p className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
              <CalendarCheck size={13} strokeWidth={1.75} className="text-[#14C07A]" />
              Toegewezen
            </p>
            <div className="flex flex-col gap-2.5">
              {TOEGEWEZEN.map((j) => (
                <div key={j.nr} className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] tabular-nums text-[var(--ink-3)]">{j.nr}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_PILL[j.status]}`}>
                      {j.status}
                    </span>
                  </div>
                  <p className="mt-1.5 flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)]">
                    <WashingMachine size={14} strokeWidth={1.75} className="text-[var(--ink-3)]" />
                    {j.apparaat} · {j.merk}
                  </p>
                  <p className="mt-0.5 text-[12px] italic text-[var(--ink-2)]">&ldquo;{j.storing}&rdquo;</p>
                  <div className="mt-1.5 flex items-center justify-between text-[12px] text-[var(--ink-2)]">
                    <span className="flex items-center gap-1">
                      <Clock size={12} strokeWidth={1.75} className="text-[var(--ink-3)]" />
                      {j.tijd}
                    </span>
                    <span className="font-mono font-semibold tabular-nums text-[var(--ink)]">{euro(j.tarief)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-[11px] text-[var(--ink-3)]">{j.plaats}</p>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0D7A4A]">
                      <ShieldCheck size={13} strokeWidth={1.75} />
                      Binnen garantie
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aanbieding van EP Heinz */}
          <div>
            <p className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
              <Inbox size={13} strokeWidth={1.75} className="text-[#4A5BD9]" />
              Aanbieding
            </p>
            <div className="rounded-xl border border-[#4A5BD9]/30 bg-[#EEF0FF]/40 p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tabular-nums text-[var(--ink-3)]">EH-0231</span>
                <span className="rounded-full bg-[#EEF0FF] px-2 py-0.5 text-[10px] font-semibold text-[#4A5BD9]">
                  Aangeboden aan jou
                </span>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-[14px] font-semibold text-[var(--ink)]">
                <WashingMachine size={15} strokeWidth={1.75} className="text-[var(--ink-3)]" />
                Droger · AEG
              </p>
              <p className="mt-0.5 text-[12px] italic text-[var(--ink-2)]">&ldquo;Wordt niet warm&rdquo;</p>
              <p className="mt-0.5 text-[12px] text-[var(--ink-2)]">Van Producent X · Houten · 4,2 km</p>
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#0D7A4A]">
                <ShieldCheck size={13} strokeWidth={1.75} />
                Binnen garantie
              </p>

              <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-[var(--surface)] px-3 py-2.5">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]">Wanneer</p>
                  <p className="text-[12px] font-medium text-[var(--ink)]">Donderdagmiddag</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]">Tarief</p>
                  <p className="font-mono text-[12px] font-semibold tabular-nums text-[var(--ink)]">€94</p>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[var(--brand)] px-4 py-2 text-[12px] font-semibold text-[#0B0E0C]">
                  <Check size={14} strokeWidth={2.25} />
                  Accepteren
                </span>
                <span className="inline-flex items-center justify-center rounded-full border border-[var(--line)] px-3 py-2 text-[var(--ink-3)]">
                  <X size={14} strokeWidth={2} />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Verdiensten-strip */}
        <div className="border-t border-[var(--line)] bg-[var(--surface)] px-4 py-3.5 sm:px-6 sm:py-4">
          {/* Mobiel: label boven, bedragen in een 3-koloms raster — op één rij lopen ze over. */}
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
              <Wallet size={13} strokeWidth={1.75} className="text-[#E3852A]" />
              Verdiensten
            </p>
            <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-10">
              {VERDIENSTEN.map((v) => (
                <div key={v.label} className="text-left sm:text-right">
                  <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]">{v.label}</p>
                  <p className="font-mono text-[15px] font-extrabold tabular-nums text-[var(--ink)]">{euro(v.bedrag)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
